import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { posForOffset } from './textOffsets';

/**
 * Tints a stretch of text that has just arrived — a dictation, while it is
 * spoken and for a moment after — so the eye finds what is new.
 *
 * A decoration like the Befund chips (see FindingRefDecoration): it paints over
 * the document and never changes it. The range is in plain-text offsets, the
 * same ones the field's value has.
 */
export const CLASS_FRESH = 'ast-fresh-text';

export interface FreshRange {
    start: number;
    end: number;
}

interface FreshStorage {
    range: FreshRange | null;
}

declare module '@tiptap/core' {
    interface Storage {
        freshTextDecoration: FreshStorage;
    }
    interface Commands<ReturnType> {
        freshTextDecoration: {
            /** Tint this range from now on; null clears it. */
            setFreshRange: (range: FreshRange | null) => ReturnType;
        };
    }
}

const freshPluginKey = new PluginKey('ast-fresh-text');

export const FreshTextDecoration = Extension.create<
    Record<string, never>,
    FreshStorage
>({
    name: 'freshTextDecoration',

    addStorage() {
        return { range: null };
    },

    addCommands() {
        return {
            setFreshRange:
                (range: FreshRange | null) =>
                ({ tr, dispatch }) => {
                    this.storage.range = range;
                    if (dispatch) {
                        // Nothing in the document changed; the redraw has to
                        // be asked for.
                        tr.setMeta(freshPluginKey, true);
                    }
                    return true;
                }
        };
    },

    addProseMirrorPlugins() {
        const storage = this.storage;
        return [
            new Plugin({
                key: freshPluginKey,
                props: {
                    decorations(state) {
                        const range = storage.range;
                        if (!range || range.end <= range.start) {
                            return DecorationSet.empty;
                        }
                        const { doc } = state;
                        const from = posForOffset(doc, range.start);
                        const to = posForOffset(doc, range.end);
                        if (to <= from) {
                            return DecorationSet.empty;
                        }
                        return DecorationSet.create(doc, [
                            Decoration.inline(from, to, { class: CLASS_FRESH })
                        ]);
                    }
                }
            })
        ];
    }
});
